import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LineItemCacheService } from '@galaxy/inventory-ui';
import { TranslateService } from '@ngx-translate/core';
import { BlameDeactivationReasons } from '@vendasta/accounts/legacy';
import { EditionChangeInformation, OrderItemParameters, Reasons } from '@vendasta/order-fulfillment';
import { LineItem, Order } from '@vendasta/sales-orders';
import moment from 'moment';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ActivationSettingsApiService } from '../activation/activation-settings-api.service';
import { ActivateAppService } from '../activation/core/activate-app/activate-app.service';
import { ActivationService } from '../activation/core/activation.service';
import { PendingActivationsService } from '../activation/core/pending-activations.service';
import { ProductOrderFormStoreService } from '../activation/core/product-order-form.store.service';
import { AccountService } from '../core/account.service';
import { User } from '../core/user';
import { VConfigService } from '../vconfig/vconfig.service';

export interface ChangeEditionDialogDataInterface {
  appId: string;
  appName: string;
  appIconUrl: string;
  currentEditionId: string;
  activationId: string;
  hasUWMEdition$: Observable<boolean>;
  reasons: BlameDeactivationReasons;
  autoSelectEdition: string;
  isTrial: boolean;
  dependentProductNames: string[];
}

export interface EditionInfo {
  editionTitle: string;
  editionId: string;
}

export interface ChangeEditionRequest {
  activationId: string;
  appId: string;
  newEditionId: string;
  reasons?: BlameDeactivationReasons;
}

@Injectable({ providedIn: 'root' })
export class ChangeEditionDialogService {
  orderConfirmationUrl = '/businesses/accounts/{}/activation/confirmation';

  users$$ = new ReplaySubject<User[]>(1);
  users$ = this.users$$.asObservable();

  constructor(
    private accountService: AccountService,
    private activateAppService: ActivateAppService,
    private pendingActivationService: PendingActivationsService,
    private activationService: ActivationService,
    private router: Router,
    private translate: TranslateService,
    private productStoreService: ProductOrderFormStoreService,
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    @Inject('MARKET_ID') private readonly marketId$: Observable<string>,
    private vconfigService: VConfigService,
    private activationSettingsApiService: ActivationSettingsApiService,
    private lineItemCacheService: LineItemCacheService,
  ) {
    combineLatest([this.accountService.users$, this.accountService.loadingUsers$])
      .pipe(
        filter(([_, loading]) => !loading),
        shareReplay(1),
      )
      .subscribe(([users, _]) => {
        this.users$$.next(users || []);
      });
  }

  /**
   * Activate/Change edition for an App.
   *
   * @param appId - ID of the app to change edition for.
   * @param activationId - ID of the activation we're changing edition for.
   * @param selectedEditionId - Edition to change to.
   * @param reasons - why they are performing this action, from the downgrade workflow
   * @param emailSmbUsersOnActivation - whether the SMBs on the account should be emailed upon successful activation
   * @return An observable that emits true if the order for edition change was created, false if it failed.
   */
  activateEditionForApp(
    appId: string,
    activationId: string,
    selectedEditionId: string,
    reasons?: BlameDeactivationReasons,
    emailSmbUsersOnActivation?: boolean,
  ): Observable<boolean> {
    return this.accountService.accountID$.pipe(
      switchMap((businessId) =>
        this.activationSettingsApiService.setNotificationSettings(businessId, [appId], emailSmbUsersOnActivation),
      ),
      switchMap((_) => this.changeEditionForApp(appId, activationId, selectedEditionId, reasons)),
      tap(() => {
        this.accountService.reloadProducts();
      }),
    );
  }

  /**
   * Using OrderFulfillmentService, change edition for an app.
   *
   * @param itemId - ID of the app to change edition for.
   * @param activationId - ID of the activation we're changing edition for.
   * @param editionId - Edition to change to.
   * @param reasons - User input describing why they changed edition, from the downgrade workflow
   * @return An observable that emits true if it created the order for changing edition, false if it failed.
   */
  changeEditionForApp(
    itemId: string,
    activationId: string,
    editionId: string,
    reasons: BlameDeactivationReasons,
  ): Observable<boolean> {
    return combineLatest([this.accountService.accountID$, this.partnerId$]).pipe(
      take(1),
      switchMap(([businessId, partnerId]) => {
        if (!businessId) {
          throw new Error('Business ID not valid');
        }
        const editionChangeInformation = new EditionChangeInformation({
          currentActivationId: activationId,
        });

        if (reasons) {
          editionChangeInformation.editionChangeReasons = new Reasons({
            choices: reasons.choices,
            comments: reasons.comments,
          });
        }

        const orderItemParams = new OrderItemParameters({
          businessId,
          itemId,
          editionId,
          editionChangeInformation,
        });

        return this.activateAppService.createOrder(partnerId, [orderItemParams]).pipe(
          map((orderId) => {
            return !!orderId;
          }),
        );
      }),
    );
  }

  changeEditions(changeRequests: ChangeEditionRequest[]): Observable<boolean> {
    return combineLatest([this.accountService.accountID$, this.partnerId$]).pipe(
      take(1),
      switchMap(([businessId, partnerId]) => {
        if (!businessId) {
          throw new Error('Business ID not valid');
        }
        const orderItems: OrderItemParameters[] = changeRequests.map((changeRequest) => {
          const editionChangeInformation = new EditionChangeInformation({
            currentActivationId: changeRequest.activationId,
          });
          if (changeRequest.reasons) {
            const reasons = changeRequest.reasons;
            editionChangeInformation.editionChangeReasons = new Reasons({
              choices: reasons.choices,
              comments: reasons.comments,
            });
          }
          const orderItemParams = new OrderItemParameters({
            businessId,
            itemId: changeRequest.appId,
            editionId: changeRequest.newEditionId,
            editionChangeInformation,
          });
          return orderItemParams;
        });
        return this.activateAppService.createOrder(partnerId, orderItems).pipe(map((orderId) => !!orderId));
      }),
      tap(() => {
        this.accountService.reloadProducts();
      }),
    );
  }

  handleTrialUpgradeToPaid(appId: string, editionId: string): void {
    combineLatest([this.accountService.accountID$, this.partnerId$, this.marketId$])
      .pipe(take(1))
      .subscribe(([accountGroupId, partnerId, marketId]) => {
        this.activationService.selectItem({
          appId: appId,
          editionId: editionId,
          quantity: 1,
        });

        const lineItems = new LineItem({
          appKey: {
            appId: appId,
            editionId: editionId,
          },
          quantity: 1,
          // Default to free for now as we are lacking retail pricing here.
          currentRevenue: {
            revenueComponents: [{}],
          },
        });

        const order = new Order({
          businessId: accountGroupId,
          marketId: marketId,
          partnerId: partnerId,
          requestedActivation: moment().toDate(),
          lineItems: [lineItems],
          tags: [this.translate.instant('SALES_ORDERS.MANUAL_ACTIVATION_TAG')],
        });

        this.productStoreService.cacheOrder(order);
        this.lineItemCacheService.cacheLineItems(accountGroupId, [
          { itemId: appId, editionId: editionId, itemType: 'app', quantity: 1 },
        ]);

        const queryParams = {
          accountGroupId,
          appIds: JSON.stringify([
            {
              quantity: 1,
              appId: appId,
              editionId: editionId,
            },
          ]),
          navigationSource: 'change-edition-dialog',
          addonIds: '[]',
          partnerId,
        };
        const url = this.orderConfirmationUrl.replace('{}', accountGroupId);
        this.router.navigate([url], { queryParams });
      });
  }
}
